import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';

import { StepSuspense } from '../../../../../../components/csv-import/ui/components/step-suspense/step-suspense';
import { useOnComponentMount } from '../../../../../../hooks/use-on-component-mount';
import { useSellerShipStationFulfillmentProvider } from '../../../../../../hooks/use-shipstation-fulfillment-whitelabel-enabled';
import AdvancedShippingNoticeContextProvider from '../../../../advanced-shipping-notices/components/advanced-shipping-notice-drawer/advanced-shipping-notice-context';
import {
  DEFINE_SHIPMENTS_STEP,
  SELECT_PRODUCTS_STEP,
} from '../../../../advanced-shipping-notices/components/advanced-shipping-notice-drawer/steps/constants';
import { DefineShipmentsCard } from '../../../../advanced-shipping-notices/components/advanced-shipping-notice-drawer/steps/define-shipments/define-shipments-card';
import { SelectProductsCard } from '../../../../advanced-shipping-notices/components/advanced-shipping-notice-drawer/steps/select-products/select-products-card';
import { SetupConfigurationCard } from '../../../../advanced-shipping-notices/components/advanced-shipping-notice-drawer/steps/setup-configurations/setup-configurations-card';
import { initialAdvancedShippingNotice } from '../../../../advanced-shipping-notices/constants';
import { getAsnWithId } from '../../../../fulfillments.api';
import { STEPS } from '../index';
import { bodyMessages } from './messages';
import styles from './styles.module.less';

/**
 * @param {import('../../types').BodyProps<import('../../types').SendProductsData>} props
 * @returns {React.JSX.Element}
 */
export const Body = ({ asnId, updateWizardStep }) => {
  const selectProductsRef = useRef(null);
  const defineShipmentsRef = useRef(null);
  const [nextStepId, setNextStepId] = useState(null);

  const saveAsn = useCallback(
    /**
     * @param {string} id
     * @param {boolean} isComplete
     * @returns {void}
     */
    (id, isComplete) => updateWizardStep({ asnId: id, isComplete }, STEPS.sendProducts),
    [updateWizardStep],
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [initialAsn, setInitialAsn] = useState();

  const { sellerFulfillmentProviderId } = useSellerShipStationFulfillmentProvider() || {};

  const fetchAsn = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const data = await getAsnWithId(sellerFulfillmentProviderId, asnId);
      setInitialAsn(data);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [asnId, sellerFulfillmentProviderId]);

  useOnComponentMount(() => {
    if (asnId) {
      fetchAsn();
    } else {
      setInitialAsn({
        ...initialAdvancedShippingNotice,
        purchaseOrderNumber: GuidService.newGuid(),
      });
      setIsLoading(false);
    }
  });

  useEffect(() => {
    if (!nextStepId) return;
    const scrollOptions = { behavior: 'smooth', block: 'center', inline: 'nearest' };

    switch (nextStepId) {
      case SELECT_PRODUCTS_STEP:
        selectProductsRef.current?.scrollIntoView(scrollOptions);
        break;
      case DEFINE_SHIPMENTS_STEP:
        defineShipmentsRef.current?.scrollIntoView(scrollOptions);
        break;
      default:
        break;
    }

    setNextStepId(null);
  }, [nextStepId]);

  const handleScrollToNextSection = (stepId) => {
    if (stepId === null) {
      return;
    }
    setNextStepId(stepId);
  };

  const trackAsnWorkflow = () => {
    // To be implemented in future task
  };

  if (isError) {
    return (
      <div>
        <Text textType={TextType.Paragraph} value={bodyMessages.tryAgain} />
        <Button className={styles['try-again-btn']} onClick={fetchAsn} theme={THEME_CONSTANTS.tertiary}>
          <FormattedMessage {...bodyMessages.errorInfo} />
        </Button>
      </div>
    );
  }

  return (
    <StepSuspense isLoading={isLoading}>
      <FlexColumn className={styles['send-products-body']}>
        <AdvancedShippingNoticeContextProvider
          initialAsn={initialAsn}
          onSaveStep={saveAsn}
          scrollToNextSection={handleScrollToNextSection}
          trackAsnWorkflow={trackAsnWorkflow}
        >
          <FlexColumn className={styles['asn-form-container']}>
            <div>
              <SetupConfigurationCard />
            </div>
            <div ref={selectProductsRef}>
              <SelectProductsCard />
            </div>
            <div ref={defineShipmentsRef}>
              <DefineShipmentsCard />
            </div>
          </FlexColumn>
        </AdvancedShippingNoticeContextProvider>
      </FlexColumn>
    </StepSuspense>
  );
};
